import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  BarChart3, Users, Eye, TrendingUp, Clock, Church, 
  CheckCircle2, XCircle, Loader2, PieChart as PieChartIcon 
} from "lucide-react";
import { getPollAnalytics } from "@/lib/api";
import { toast } from "sonner";
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  AreaChart, Area, XAxis, YAxis, Tooltip, 
  BarChart, Bar, Legend 
} from "recharts";

const CHART_COLORS = [
  'var(--liturgical-primary)',
  'var(--liturgical-secondary)',
  '#4A90A4',
  '#50C878',
  '#FFB347',
  '#FF6B6B',
  '#9B59B6',
  '#3498DB',
  '#E67E22',
  '#1ABC9C'
];

function StatCard({ icon: Icon, label, value, subtext, color = "var(--liturgical-primary)" }) {
  return (
    <div className="bg-white rounded-lg border border-[#E5E0D5] p-4">
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#2D2A26] font-['Crimson_Text']">{value}</p>
          <p className="text-xs text-[#6B665E] font-['Nunito']">{label}</p>
          {subtext && <p className="text-[10px] text-[#9B9590] font-['Nunito']">{subtext}</p>}
        </div>
      </div>
    </div>
  );
}

function OptionResultBar({ option, index, maxVotes }) {
  const width = maxVotes > 0 ? (option.vote_count / maxVotes) * 100 : 0;
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-sm font-['Nunito'] text-[#2D2A26] truncate flex-1 mr-2">
          {option.text}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold font-['Nunito']" style={{ color: CHART_COLORS[index % CHART_COLORS.length] }}>
            {option.percentage}%
          </span>
          <span className="text-xs text-[#6B665E] font-['Nunito']">
            ({option.vote_count})
          </span>
        </div>
      </div>
      <div className="h-3 bg-[#F0EBE0] rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-500"
          style={{ 
            width: `${width}%`,
            backgroundColor: CHART_COLORS[index % CHART_COLORS.length]
          }}
        />
      </div>
    </div>
  );
}

export default function PollAnalyticsDialog({ postId, open, onClose }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && postId) {
      loadAnalytics();
    }
  }, [open, postId]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await getPollAnalytics(postId);
      setAnalytics(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load analytics");
      onClose();
    }
    setLoading(false);
  };

  const formatTimelineLabel = (timeStr) => {
    if (!timeStr) return "";
    const date = new Date(timeStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric' });
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-2xl text-[#2D2A26] flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[var(--liturgical-primary)]" />
            Poll Analytics
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
          </div>
        ) : analytics ? (
          <div className="space-y-6">
            {/* Poll Question */}
            <div className="bg-[#F9F7F2] rounded-lg p-4 border border-[#E5E0D5]">
              <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26]">
                {analytics.question}
              </h3>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <Badge 
                  className={`text-xs font-['Nunito'] ${analytics.is_closed ? 'bg-[#6B665E]' : 'bg-[var(--liturgical-primary)]'} text-white`}
                >
                  {analytics.is_closed ? 'Closed' : 'Active'}
                </Badge>
                <span className="text-xs text-[#6B665E] font-['Nunito']">
                  Duration: {analytics.duration_days} days
                </span>
                {analytics.settings.allow_multiple && (
                  <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-xs">
                    Multiple Choice
                  </Badge>
                )}
                {analytics.settings.anonymous ? (
                  <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-xs">
                    Anonymous
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-xs">
                    Public
                  </Badge>
                )}
              </div>
            </div>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard 
                icon={Eye} 
                label="Total Views" 
                value={analytics.engagement.total_views}
                color="#4A90A4"
              />
              <StatCard 
                icon={Users} 
                label="Total Voters" 
                value={analytics.engagement.total_voters}
                color="var(--liturgical-primary)"
              />
              <StatCard 
                icon={CheckCircle2} 
                label="Total Votes" 
                value={analytics.engagement.total_votes}
                subtext={`${analytics.engagement.votes_per_voter} votes/voter`}
                color="#50C878"
              />
              <StatCard 
                icon={TrendingUp} 
                label="Engagement Rate" 
                value={`${analytics.engagement.engagement_rate}%`}
                subtext="of viewers voted"
                color="var(--liturgical-secondary)"
              />
            </div>

            {/* Results Section */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Bar Results */}
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Crimson_Text'] text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Results Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {analytics.options.map((option, index) => (
                    <OptionResultBar 
                      key={index} 
                      option={option} 
                      index={index}
                      maxVotes={Math.max(...analytics.options.map(o => o.vote_count))}
                    />
                  ))}
                </CardContent>
              </Card>

              {/* Pie Chart */}
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Crimson_Text'] text-lg flex items-center gap-2">
                    <PieChartIcon className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Vote Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {analytics.engagement.total_votes > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={analytics.options}
                          dataKey="vote_count"
                          nameKey="text"
                          cx="50%"
                          cy="50%"
                          outerRadius={70}
                          label={({ text, percentage }) => `${percentage}%`}
                          labelLine={false}
                        >
                          {analytics.options.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name) => [`${value} votes`, name]}
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #E5E0D5',
                            borderRadius: '8px',
                            fontFamily: 'Nunito'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[200px] flex items-center justify-center text-[#6B665E] font-['Nunito']">
                      No votes yet
                    </div>
                  )}
                  {/* Legend */}
                  <div className="flex flex-wrap gap-2 mt-2 justify-center">
                    {analytics.options.map((option, index) => (
                      <div key={index} className="flex items-center gap-1">
                        <div 
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                        />
                        <span className="text-xs text-[#6B665E] font-['Nunito'] truncate max-w-[80px]">
                          {option.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Voting Timeline */}
            {analytics.vote_timeline && analytics.vote_timeline.length > 0 && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Crimson_Text'] text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Voting Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={analytics.vote_timeline}>
                      <defs>
                        <linearGradient id="voteGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--liturgical-primary)" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="var(--liturgical-primary)" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis 
                        dataKey="time" 
                        tickFormatter={formatTimelineLabel}
                        tick={{ fontSize: 10, fontFamily: 'Nunito', fill: '#6B665E' }}
                        axisLine={{ stroke: '#E5E0D5' }}
                        tickLine={{ stroke: '#E5E0D5' }}
                      />
                      <YAxis 
                        tick={{ fontSize: 10, fontFamily: 'Nunito', fill: '#6B665E' }}
                        axisLine={{ stroke: '#E5E0D5' }}
                        tickLine={{ stroke: '#E5E0D5' }}
                        allowDecimals={false}
                      />
                      <Tooltip 
                        labelFormatter={formatTimelineLabel}
                        formatter={(value) => [`${value} votes`, 'Votes']}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #E5E0D5',
                          borderRadius: '8px',
                          fontFamily: 'Nunito'
                        }}
                      />
                      <Area 
                        type="monotone" 
                        dataKey="votes" 
                        stroke="var(--liturgical-primary)" 
                        strokeWidth={2}
                        fill="url(#voteGradient)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Parish Breakdown */}
            {analytics.parish_breakdown && analytics.parish_breakdown.length > 0 && (
              <Card className="bg-white border-[#E5E0D5]">
                <CardHeader className="pb-2">
                  <CardTitle className="font-['Crimson_Text'] text-lg flex items-center gap-2">
                    <Church className="w-5 h-5 text-[var(--liturgical-primary)]" />
                    Voter Demographics by Parish
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Bar Chart */}
                    <ResponsiveContainer width="100%" height={Math.max(150, analytics.parish_breakdown.length * 35)}>
                      <BarChart 
                        data={analytics.parish_breakdown} 
                        layout="vertical"
                        margin={{ left: 0, right: 20 }}
                      >
                        <XAxis 
                          type="number"
                          tick={{ fontSize: 10, fontFamily: 'Nunito', fill: '#6B665E' }}
                          axisLine={{ stroke: '#E5E0D5' }}
                          allowDecimals={false}
                        />
                        <YAxis 
                          type="category" 
                          dataKey="parish" 
                          tick={{ fontSize: 10, fontFamily: 'Nunito', fill: '#6B665E' }}
                          axisLine={{ stroke: '#E5E0D5' }}
                          width={120}
                        />
                        <Tooltip 
                          formatter={(value, name, props) => [`${value} voters (${props.payload.percentage}%)`, 'Voters']}
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #E5E0D5',
                            borderRadius: '8px',
                            fontFamily: 'Nunito'
                          }}
                        />
                        <Bar dataKey="count" fill="var(--liturgical-primary)" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                    
                    {/* Text Summary */}
                    <div className="space-y-2">
                      {analytics.parish_breakdown.map((parish, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-[#F9F7F2] rounded-lg">
                          <span className="text-sm font-['Nunito'] text-[#2D2A26]">{parish.parish}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-[var(--liturgical-primary)] font-['Nunito']">
                              {parish.count}
                            </span>
                            <span className="text-xs text-[#6B665E] font-['Nunito']">
                              ({parish.percentage}%)
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
